import { lang } from '@umu/ukit-lang';
/**
 * @description 迁移自 textLimitCheck
 * @author librajt
 */

/**
 * 包含字数控制的textarea，当字数达到 maxLength - 100 时显示字数统计，超出时提示文案
 * 自带dom结构，生成的提示和文本框dom平级，处理样式时请注意
 * Example:
 *
 * constructor:
 *   this.textareaRef = createRef();
 * render:
 *   <Textarea className="commentData" placeholder="<%-lang_12078%>" maxLength="2000" onChange={this.onChange} />
 *
 * 取值、设置值
 *   通过 onChange 同步数据
 *
 * 设置 focus
 *   通过 isFocused 参数设置
 *
 */

import React, { Component, createRef, RefObject } from 'react';
import { classMapSet, getFixBreakLength } from '@umu/umu-util';
import { isAndroid } from '@umu/umu-ua';
import './textarea.scss';
import If from '../../if';

export interface IProps {
    isDisabled: boolean;
    fixKeyboard: boolean;
    isAutoExtend: boolean;
    isFocused: boolean;
    placeholder: string;
    value: string;
    maxLength: number;
    className?: string;
    style?: { [key: string]: any };
    showCharCount?: boolean;
    onChange(value: string): void;
    onFocus?(e: React.FocusEvent<HTMLTextAreaElement>): void;
    onBlur?(value?: string): void;
}

export interface IState {
    valueLength: number;
    shouldFocus: boolean;
}

class Textarea extends Component<IProps, IState> {
    public static defaultProps: Partial<IProps> = {
        maxLength: 2000,
    };

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState) {
        return {
            shouldFocus: nextProps.isFocused !== prevState.shouldFocus,
            valueLength: (nextProps.value && nextProps.value.length) || 0,
        };
    }

    private textareaRef: RefObject<HTMLTextAreaElement> = createRef();
    private textareaPreRef: RefObject<HTMLPreElement> = createRef();

    constructor(props: IProps) {
        super(props);

        this.state = {
            valueLength: 0,
            shouldFocus: false,
        };

        this.onBlur = this.onBlur.bind(this);
        this.focus = this.focus.bind(this);
        this.onInputFocus = this.onInputFocus.bind(this);
        this.getTextLength = this.getTextLength.bind(this);
    }

    public updatePlaceHolder() {
        const textarea = this.textareaRef.current;
        if (!textarea) {
            return;
        }
        const pre = this.textareaPreRef.current;
        if (!pre) {
            return;
        }

        if (!textarea.value && textarea.placeholder /* IE<9 时没有这个属性 */) {
            const placeholder = textarea.placeholder.replace(/\\n/g, '\n');
            textarea.placeholder = placeholder;
            pre.innerHTML = placeholder;
        }
    }

    public componentDidMount() {
        const textareaPre = this.textareaPreRef.current;
        if (!textareaPre) {
            return;
        }

        const props = this.props;
        if (props.isAutoExtend) {
            textareaPre.innerHTML = props.value || props.placeholder;
            this.updatePlaceHolder();
        }
    }

    public componentDidUpdate(_prevProps: IProps, prevState: IState) {
        const props = this.props;
        if (props.isAutoExtend) {
            this.updatePlaceHolder();
        }

        if (prevState.shouldFocus !== this.state.shouldFocus) {
            this.focus();
        }
    }

    public getTextLength(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const valueLength = getFixBreakLength(value);
        this.setState({
            valueLength,
        });
        if (this.props.onChange) {
            this.props.onChange(value);
        }

        const props = this.props;
        if (props.isAutoExtend) {
            const preEl = this.textareaPreRef.current;
            if (preEl) {
                preEl.innerHTML = value || props.placeholder;
            }
        }
    }

    public onBlur(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        if (this.props.onBlur) {
            this.props.onBlur(value);
        }
    }

    public focus() {
        const textarea = this.textareaRef.current;
        if (!textarea) {
            return;
        }
        textarea.focus();
        this.setState({
            shouldFocus: false,
        });
    }

    public onInputFocus(e: React.FocusEvent<HTMLTextAreaElement>) {
        if (this.props.onFocus) {
            this.props.onFocus(e);
        }
        if (!this.props.fixKeyboard || !isAndroid) {
            return;
        }
        const target = e.target;
        if (!target) {
            return;
        }
        setTimeout(() => {
            // 安卓上键盘弹起后被遮挡的修复
            if (target.scrollIntoView) {
                target.scrollIntoView();
            }
        }, 500);
    }

    public renderTextLimit() {
        const { showCharCount } = this.props;
        const maxLength = this.props.maxLength;
        const valueLength = this.state.valueLength;

        let text = '';

        // 达到最大值前100字符时显示
        if ((showCharCount || valueLength >= maxLength - 100) && valueLength < maxLength) {
            text = valueLength + ' / ' + maxLength;
        } else if (valueLength < maxLength) {
            text = '';
        } else {
            text = lang('文字已达到字数限制');
        }

        if (text) {
            return <div className="length-limit">{text}</div>;
        } else {
            return null;
        }
    }

    public render() {
        const props = this.props;
        const className: { [key: string]: boolean } = {
            'textarea-holder': true,
        };
        if (props.className) {
            className[props.className] = true;
        }
        Object.assign(className, { 'auto-extend': props.isAutoExtend });

        const textareaStyle = Object.assign({}, props.style);

        return (
            <div className={classMapSet(className)}>
                <div className="input">
                    <If condition={props.isAutoExtend}>
                        <pre ref={this.textareaPreRef}>{props.value || props.placeholder}</pre>
                    </If>
                    <If condition={props.isDisabled}>
                        <textarea
                            autoComplete="off"
                            maxLength={props.maxLength || 2000}
                            placeholder={props.placeholder}
                            ref={this.textareaRef}
                            value={this.props.value}
                            disabled={true}
                            style={textareaStyle}
                            spellCheck={false}
                        ></textarea>
                    </If>
                    <If condition={!props.isDisabled}>
                        <textarea
                            autoComplete="off"
                            maxLength={props.maxLength || 2000}
                            placeholder={props.placeholder}
                            onChange={this.getTextLength}
                            onBlur={this.onBlur}
                            onFocus={this.onInputFocus}
                            ref={this.textareaRef}
                            defaultValue={this.props.value}
                            style={textareaStyle}
                            spellCheck={false}
                        ></textarea>
                    </If>
                </div>
                {this.renderTextLimit()}
            </div>
        );
    }
}

export default Textarea;
